// PlanView Component - Plan mode with view toggle (Cards | Gantt | Focus)
import React from 'react';
import { Plus, LayoutGrid, BarChart3, Target } from 'lucide-react';
import { ABLE_COLORS } from '../constants';
import SegmentedControl from './SegmentedControl';
import FocusView from './FocusView';
import useLocalStorage from '../hooks/useLocalStorage';

const VIEW_KEYS = { CARDS: 'cards', GANTT: 'gantt', FOCUS: 'focus' };

const SUB_VIEWS = [
  { key: VIEW_KEYS.CARDS, label: 'Cards', icon: LayoutGrid },
  { key: VIEW_KEYS.GANTT, label: 'Gantt', icon: BarChart3 },
  { key: VIEW_KEYS.FOCUS, label: 'Focus', icon: Target },
];

const STORAGE_KEY = 'mabel-plan-subview';

export default function PlanView({
  filteredTasks,
  capacity,
  newTaskTitle,
  setNewTaskTitle,
  onAddTask,
  renderTaskCard,
  handleDragOver,
  handleDrop,
  // Focus mode props
  currentTask,
  focusIndex,
  isCardFlipped,
  setIsCardFlipped,
  onSkip,
  onMarkDone
}) {
  const [subView, setSubView] = useLocalStorage(STORAGE_KEY, VIEW_KEYS.CARDS);

  // Validate stored value against known keys
  const validSubView = SUB_VIEWS.some(v => v.key === subView) ? subView : VIEW_KEYS.CARDS;

  return (
    <>
      {/* View Toggle */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '20px',
      }}>
        <SegmentedControl
          options={SUB_VIEWS}
          value={validSubView}
          onChange={setSubView}
        />
      </div>

      {/* Cards Sub-View (default) */}
      {validSubView === VIEW_KEYS.CARDS && (
        <>
          {/* Add Task Input */}
          <div style={{
            background: ABLE_COLORS.neutral,
            borderRadius: ABLE_COLORS.cardRadiusLarge,
            padding: '16px',
            marginBottom: '20px',
            boxShadow: ABLE_COLORS.cardShadow,
            border: `2px solid ${ABLE_COLORS.borderLight}`,
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap'
          }}>
            <input
              type="text"
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onAddTask();
                }
              }}
              placeholder="Add a task..."
              style={{
                flex: 1,
                minWidth: '200px',
                padding: '12px 16px',
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.radiusMedium,
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            <button
              onClick={onAddTask}
              disabled={!newTaskTitle.trim()}
              style={{
                padding: '12px 20px',
                background: newTaskTitle.trim() ? ABLE_COLORS.primary : ABLE_COLORS.border,
                color: 'white',
                border: 'none',
                borderRadius: ABLE_COLORS.radiusMedium,
                cursor: newTaskTitle.trim() ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: '600',
                fontSize: '16px',
                opacity: newTaskTitle.trim() ? 1 : 0.5,
                boxShadow: newTaskTitle.trim() ? `0 4px 12px ${ABLE_COLORS.primary}30` : 'none',
                transition: 'all 0.2s ease',
                flexShrink: 0
              }}
            >
              <Plus size={20} />
              <span className="add-btn-text">Add</span>
            </button>
            <style>{`
              @media (max-width: 360px) {
                .add-btn-text { display: none; }
              }
            `}</style>
          </div>

          {/* Task List Container */}
          <div style={{
            background: ABLE_COLORS.neutral,
            borderRadius: ABLE_COLORS.cardRadiusLarge,
            padding: '24px',
            boxShadow: ABLE_COLORS.cardShadow,
            border: `2px solid ${ABLE_COLORS.borderLight}`
          }}>
            {capacity.priority.length > 0 ? (
              capacity.priority.includes('Scheduled') ? (
                // Scheduled View - sorted by deadline
                <>
                  <h2 style={{
                    margin: '0 0 20px 0',
                    fontSize: '16px',
                    color: ABLE_COLORS.textSecondary,
                    fontWeight: '600'
                  }}>
                    Scheduled ({filteredTasks.length})
                  </h2>
                  {filteredTasks.length === 0 ? (
                    <p style={{ color: ABLE_COLORS.textMuted, textAlign: 'center', padding: '40px 20px' }}>
                      No scheduled tasks
                    </p>
                  ) : (
                    [...filteredTasks]
                      .sort((a, b) => {
                        if (!a.deadline) return 1;
                        if (!b.deadline) return -1;
                        return new Date(a.deadline) - new Date(b.deadline);
                      })
                      .map(renderTaskCard)
                  )}
                </>
              ) : (
                // Grouped by Priority View - with drag-and-drop zones
                capacity.priority.map(priority => {
                  const priorityTasks = filteredTasks.filter(t => t.priority === priority);

                  return (
                    <div
                      key={priority}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, priority)}
                      style={{ marginBottom: '32px' }}
                    >
                      <h2 style={{
                        margin: '0 0 16px 0',
                        fontSize: '16px',
                        color: ABLE_COLORS.textSecondary,
                        fontWeight: '600'
                      }}>
                        {priority} ({priorityTasks.length})
                      </h2>
                      {priorityTasks.length === 0 ? (
                        <p style={{ color: ABLE_COLORS.textMuted, textAlign: 'center', padding: '20px' }}>
                          No tasks in {priority}
                        </p>
                      ) : (
                        priorityTasks.map(renderTaskCard)
                      )}
                    </div>
                  );
                })
              )
            ) : (
              // Flat "All Tasks" View
              <>
                <h2 style={{
                  margin: '0 0 20px 0',
                  fontSize: '16px',
                  color: ABLE_COLORS.textSecondary,
                  fontWeight: '600'
                }}>
                  All Tasks ({filteredTasks.length})
                </h2>
                {filteredTasks.length === 0 ? (
                  <p style={{ color: ABLE_COLORS.textMuted, textAlign: 'center', padding: '40px 20px' }}>
                    No tasks match your current filters
                  </p>
                ) : (
                  filteredTasks.map(renderTaskCard)
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Gantt placeholder */}
      {validSubView === VIEW_KEYS.GANTT && (
        <div style={{
          background: ABLE_COLORS.neutral,
          borderRadius: ABLE_COLORS.cardRadiusLarge,
          padding: '60px 32px',
          boxShadow: ABLE_COLORS.cardShadow,
          border: `2px solid ${ABLE_COLORS.borderLight}`,
          textAlign: 'center',
        }}>
          <BarChart3 size={48} color={ABLE_COLORS.textMuted} style={{ marginBottom: '16px' }} />
          <h2 style={{
            margin: '0 0 8px 0',
            fontSize: '20px',
            color: ABLE_COLORS.textPrimary,
            fontWeight: '600',
          }}>
            Gantt View
          </h2>
          <p style={{
            margin: 0,
            fontSize: '15px',
            color: ABLE_COLORS.textMuted,
            maxWidth: '360px',
            marginLeft: 'auto',
            marginRight: 'auto',
            lineHeight: '1.5',
          }}>
            Timeline visualization with dependency chains. Coming in Phase 2.
          </p>
        </div>
      )}

      {/* Focus sub-view - renders real FocusView */}
      {validSubView === VIEW_KEYS.FOCUS && (
        <FocusView
          filteredTasks={filteredTasks}
          currentTask={currentTask}
          focusIndex={focusIndex}
          isCardFlipped={isCardFlipped}
          setIsCardFlipped={setIsCardFlipped}
          onSkip={onSkip}
          onMarkDone={onMarkDone}
        />
      )}
    </>
  );
}
